import React, {useState, useEffect, useRef} from 'react';
import ReactSelect from 'react-select';
import Tooltip from 'rc-tooltip';
import {isValidEmail} from '../../../utils/isValidEmail';
import {useAlert} from './hooks';
import Alert from '../Alert';
import {
  REQ_APPROVED_VENDOR_LOOKUP_QUERY,
  REQ_APPROVERS_LOOKUP_QUERY,
  REQ_JOBLIST_LOOKUP_QUERY,
  REQ_ANALYSIS_CODE_LOOKUP_QUERY,
  CREATE_REQUISITION_MUTATION,
  CREATE_REQUISITION_ATTACHMENT_MUTATION,
  REQ_PART_CLASS_LOOKUP_QUERY,
  REQ_DEFAULT_ADDRESS_LOOKUP_QUERY,
  REQ_CURRENCY_CODE_LOOKUP_QUERY,
  REQ_COUNTRY_LOOKUP_QUERY
} from './queries';
import {useAvailableReqComps} from './hooks/useAvailableReqComps';
function RequisitionForm({
  onFormSubmitSuccess,
  onFormSubmitError,
  currentUser
}) {
  const initialState = {
    requestorID: currentUser.epicorUserID,
    companyId: currentUser.Company,
    approverID: '',
    CompanyName: '',
    Address1: '',
    Address2: '',
    Address3: '',
    City: '',
    State: '',
    Zip: '',
    Country: '',
    CC: '',
    reqDetail: [
      {
        PartNum: '',
        LineDesc: '',
        OrderQty: 1,
        UnitCost: 0,
        Taxable: false,
        Class: '',
        VendorNum: '',
        IUM: 'EA',
        JobNum: '',
        TranType: 'PUR-MTL',
        AnalysisCode: '',
        XOrderQty: 1,
        vendorOptions: [],
        Comments: '',
        DueDate: '',
        OverrideCurrency: false,
        CurrencyCode: '',
        file: null,
        fileBase64: ''
      }
    ]
  };
  // Busn Requirements - multi company reqs need to be allowed at the user level and the company level
  // And the user might have access to multiple companies BUT NOT be allowed to file requisitions in them so we can't just assume the first company in their accessible list is their home company
  const {Company: tsComp, accessibleCompanies} = currentUser;
  const homeCompany =
    accessibleCompanies.find(comp => comp.companyId === tsComp) ||
    accessibleCompanies[0];
  const userHomeCompanyOption = {
    value: homeCompany.companyId,
    label: `${homeCompany.companyId} - ${homeCompany.companyName}`
  };

  const [isLoading, setIsLoading] = useState(false);
  const onInitialRender = useRef(true);
  const [formData, setFormData] = useState(initialState);
  const [companyDropdownOptions, setCompanyDropdownOptions] = useState([
    userHomeCompanyOption
  ]);
  const [jobOptions, setJobOptions] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [partClasses, setPartClasses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [analysisCodes, setAnalysisCodes] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [formErrors, setFormErrors] = useState('');
  const [countryOptions, setCountryOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [ccError, setCcError] = useState('');
  const {
    alert: validationErrorAlert,
    displayAlert: showErrorAlert, // Used previous unfinished logic
    setDisplayAlert: setShowErrorAlert,
    configAlert: configErrorAlert,
    alertRef
  } = useAlert();

  const {
    availableReqComps,
    error: reqCompsErr,
    isLoading: reqCompsLoading,
    setError: setReqCompsErr,
    fetchAvailableReqComps
  } = useAvailableReqComps();

  // Handle Validation Error Display
  useEffect(() => {
    if (!formErrors && !reqCompsErr) return;

    const alertDismiss = () => {
      if (reqCompsErr) setReqCompsErr(null);
      if (formErrors) setFormErrors(null);
    };

    setTimeout(() => {
      // Kick this out of the main thread so the ref is actually set by the time the alert renders
      alertRef.current?.scrollIntoView();
    });

    const errorsToDisplay = reqCompsErr ? reqCompsErr : formErrors;

    return configErrorAlert('error', errorsToDisplay, alertDismiss, 10);
  }, [formErrors, reqCompsErr]);

  // Setting up Available Companies for Reqs
  const {permissionsGroups} = currentUser;
  let userCanAccessMultiCompanyReqs =
    permissionsGroups && permissionsGroups.includes('AllowICReq');

  useEffect(() => {
    if (onInitialRender.current) {
      onInitialRender.current = false;
      fetchAvailableReqComps();
    }

    if (userCanAccessMultiCompanyReqs) {
      // Format and add available companies -- don't duplicate user's home company
      const newOptions = availableReqComps.reduce((availComps, comp) => {
        if (comp.companyID === userHomeCompanyOption.value) return availComps;
        availComps.push({
          value: comp.companyID,
          label: `${comp.companyID} - ${comp.companyName}`
        });
        return availComps;
      }, companyDropdownOptions);

      setCompanyDropdownOptions(newOptions);
    }
  }, [availableReqComps]);

  // Fetch default address options
  useEffect(() => {
    async function fetchDefaultAddress() {
      if (formData.companyId !== undefined) {
        try {
          const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              query: REQ_DEFAULT_ADDRESS_LOOKUP_QUERY.loc.source.body,
              variables: {company: formData.companyId}
            })
          });
          const data = await response.json();
          const address = data.data.defAddr[0];

          setFormData(prevData => ({
            ...prevData,
            CompanyName: address.Company_Name,
            Address1: address.Company_Address1,
            Address2: address.Company_Address2,
            Address3: address.Company_Address3,
            City: address.Company_City,
            State: address.Company_State,
            Zip: address.Company_Zip,
            Country: address.Company_Country
          }));
          if (countryOptions.length > 0 && address.Company_CountryNum) {
            const matchedCountry = countryOptions.find(
              country => country.value === address.Company_CountryNum
            );
            if (matchedCountry) {
              setFormData(prevState => ({
                ...prevState,
                Country: matchedCountry.value
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching default address:', error);
        }
      }
    }

    fetchDefaultAddress();
  }, [formData.companyId, countryOptions]);

  useEffect(() => {
    async function fetchCountryOptions() {
      if (formData.companyId !== undefined) {
        try {
          const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              query: REQ_COUNTRY_LOOKUP_QUERY.loc.source.body,
              variables: {company: formData.companyId}
            })
          });
          const data = await response.json();
          const countries = data.data.reqCountry.map(country => ({
            value: country.Country_CountryNum,
            label: country.Country_Description
          }));
          setCountryOptions(countries);
        } catch (error) {
          console.error('Error fetching country options:', error);
        }
      }
    }
    fetchCountryOptions();
  }, [formData.companyId]);

  const validateForm = () => {
    if (
      !formData.CompanyName ||
      !formData.Address1 ||
      !formData.City ||
      !formData.State ||
      !formData.Zip
    ) {
      return 'All address fields are required';
    }

    let missingReqVal;
    let commentsTooLong;

    formData.reqDetail.forEach(detail => {
      if (!detail.PartNum || !detail.UnitCost || !detail.JobNum) missingReqVal = true;
      if (detail.Comments.length > 1000) commentsTooLong = true
  })

    if ( missingReqVal) return 'Each item must have a Part Number, Unit Cost, and Job Number';
    if (commentsTooLong) return 'One or more of your comment fields have too many characters'
    

    if (ccError) {
      return 'Please fix CC email field errors before submitting.';
    }

    return null;
  };

  // Fetch currency options when the override checkbox is selected
  const fetchCurrencyOptions = async () => {
    if (formData.companyId !== undefined) {
      try {
        const response = await fetch('/api/graphql', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            query: REQ_CURRENCY_CODE_LOOKUP_QUERY.loc.source.body,
            variables: {company: formData.companyId}
          })
        });
        const data = await response.json();
        const currencyData = data.data.Req_CurrencyCode.map(currency => ({
          value: currency.Currency_CurrencyCode,
          label: currency.Currency_CurrencyCode
        }));
        setCurrencyOptions(currencyData);
      } catch (error) {
        console.error('Error fetching currency options:', error);
      }
    }
  };

  const handleOverrideCurrencyChange = (e, index) => {
    const newReqDetails = [...formData.reqDetail];
    newReqDetails[index].OverrideCurrency = e.target.checked;
    setFormData({...formData, reqDetail: newReqDetails});

    if (e.target.checked) {
      fetchCurrencyOptions();
    }
  };

  const handleCompanyChange = reactSelectEvent => {
    // Handle user deleting selection by resetting to user's default company
    if (!reactSelectEvent.value) {
      setFormData(prevState => ({
        ...prevState,
        companyId: currentUser.Company
      }));
      return;
    }

    setFormData(prevState => ({
      ...prevState,
      companyId: reactSelectEvent.value
    }));
  };

  const handleInputChange = (e, index = null) => {
    const {name, value} = e.target;

    if (name === 'CC') {
      setFormData(prev => ({...prev, CC: value}));

      const splitted = value
        .split(';')
        .map(x => x.trim())
        .filter(Boolean);
      for (let addr of splitted) {
        if (!isValidEmail(addr)) {
          setCcError('One or more email addresses are invalid.');
          return;
        }
      }

      setCcError('');
      return;
    }

    if (
      [
        'CompanyName',
        'Address1',
        'Address2',
        'Address3',
        'City',
        'State',
        'Zip'
      ].includes(name)
    ) {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    } else if (index !== null) {
      const newReqDetails = [...formData.reqDetail];

      let fieldValue = value;

      if (name === 'OrderQty' || name === 'VendorNum') {
        fieldValue = parseInt(value, 10);
      } else if (name === 'UnitCost') {
        fieldValue = parseFloat(value);
      }

      if (name === 'DueDate' || name === 'Comments') {
        fieldValue = value;
      }

      newReqDetails[index][name] = fieldValue;

      // Calculate Line Total when OrderQty or UnitCost changes
      if (name === 'OrderQty' || name === 'UnitCost') {
        const orderQty = parseFloat(newReqDetails[index].OrderQty) || 0;
        const unitCost = parseFloat(newReqDetails[index].UnitCost) || 0;
        newReqDetails[index].LineTotal = orderQty * unitCost;
      }

      setFormData({...formData, reqDetail: newReqDetails});
    }
  };

  const handleTaxableChange = (e, index) => {
    const newReqDetails = [...formData.reqDetail];
    newReqDetails[index].Taxable = e.target.checked;
    setFormData({...formData, reqDetail: newReqDetails});
  };

  const handleSelectChange = (selectedOption, field, index) => {
    const newReqDetails = [...formData.reqDetail];

    if (field === 'VendorNum') {
      const selectedVendor = vendors.find(
        vendor => vendor.value === selectedOption.value
      );
      newReqDetails[index].VendorNum = selectedOption.value;
      if (selectedVendor) {
        newReqDetails[index].CurrencyCode = selectedVendor.CurrencyCode;
      }
    } else if (field === 'JobNum') {
      const selectedJob = jobOptions.find(
        job => job.value === selectedOption.value
      );
      newReqDetails[index].JobNum = selectedOption.value;

      if (selectedJob && selectedJob.ProjectManager) {
        const normalizedProjectManager = selectedJob.ProjectManager.replace(
          /[\s.]/g,
          ''
        ).toLowerCase();
        const selectedApprover = approvers.find(
          approver =>
            approver.value.replace(/[\s.]/g, '').toLowerCase() ===
            normalizedProjectManager
        );

        if (selectedApprover) {
          setFormData(prevFormData => ({
            ...prevFormData,
            reqDetail: newReqDetails,
            approverID: selectedApprover.value
          }));
        } else {
          console.log('No matching approver found for Project Manager.');
        }
      }
    } else {
      newReqDetails[index][field] = selectedOption.value;
    }

    setFormData(prevFormData => ({
      ...prevFormData,
      reqDetail: newReqDetails
    }));
  };

  const handleCountryChange = selectedOption => {
    setFormData(prevState => ({
      ...prevState,
      Country: selectedOption.value
    }));
  };

  const handleDueDateChange = (e, index) => {
    const newReqDetails = [...formData.reqDetail];
    const selectedDate = e.target.value;

    newReqDetails[index] = {
      ...newReqDetails[index],
      DueDate: selectedDate
    };

    setFormData(prevData => ({
      ...prevData,
      reqDetail: newReqDetails
    }));
  };

  const addNewLine = () => {
    setFormData(prevFormData => ({
      ...prevFormData,
      reqDetail: [
        ...prevFormData.reqDetail,
        {
          PartNum: '',
          LineDesc: '',
          OrderQty: 1,
          UnitCost: 0,
          Taxable: false,
          Class: partClasses.length > 0 ? partClasses[0].value : '',
          VendorNum: '',
          JobNum: '',
          IUM: 'EA',
          TranType: 'PUR-MTL',
          Comments: '',
          AnalysisCode: '',
          XOrderQty: 1,
          vendorOptions: [],
          DueDate: '',
          OverrideCurrency: false,
          CurrencyCode: ''
        }
      ]
    }));
  };

  const removeItem = indexToRemove => {
    const newReqDetails = formData.reqDetail.filter(
      (_, index) => index !== indexToRemove
    );
    setFormData({...formData, reqDetail: newReqDetails});
  };

  // Fetch job options
  useEffect(() => {
    async function fetchJobOptions() {
      if (formData.companyId !== undefined) {
        try {
          const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              query: REQ_JOBLIST_LOOKUP_QUERY.loc.source.body,
              variables: {company: formData.companyId}
            })
          });
          const data = await response.json();
          const jobData = data.data.jobList.map(j => ({
            value: j.JobNum,
            label: `Project: ${j.ProjectID} - ${j.ProjectDesc} | Job: ${j.JobNum} - ${j.JobDesc}`,
            ProjectManager: j.ProjectManager
          }));
          setJobOptions(jobData);
        } catch (error) {
          console.error('Error fetching job options:', error);
        }
      }
    }

    fetchJobOptions();
  }, [formData.companyId]);

  // Fetch approvers
  useEffect(() => {
    async function fetchApprovers() {
      if (formData.companyId !== undefined) {
        try {
          const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              query: REQ_APPROVERS_LOOKUP_QUERY.loc.source.body,
              variables: {company: formData.companyId}
            })
          });
          const data = await response.json();
          const approverOptions = data.data.approverList.reduce(
            (approvers, currApprover) => {
              if (currApprover.value === currentUser.epicorUserID)
                return approvers;

              approvers.push({
                value: currApprover.value,
                label: currApprover.value
              });
              return approvers;
            },
            []
          );
          setApprovers(approverOptions);
        } catch (error) {
          console.error('Error fetching approvers:', error);
        }
      }
    }

    fetchApprovers();
  }, [formData.companyId]);

  useEffect(() => {
    async function fetchPartClasses() {
      if (formData.companyId !== undefined) {
        try {
          const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              query: REQ_PART_CLASS_LOOKUP_QUERY.loc.source.body,
              variables: {company: formData.companyId}
            })
          });
          const data = await response.json();
          const partClassOptions = data.data.partClass.map(p => ({
            value: p.value,
            label: p.label
          }));
          setPartClasses(partClassOptions);

          if (partClassOptions.length > 0) {
            setFormData(prevData => ({
              ...prevData,
              reqDetail: prevData.reqDetail.map(item => ({
                ...item,
                Class: partClassOptions[0].value
              }))
            }));
          }
        } catch (error) {
          console.error('Error fetching part classes:', error);
        }
      }
    }

    fetchPartClasses();
  }, [formData.companyId]);

  // Fetch vendors
  useEffect(() => {
    async function fetchVendors() {
      if (formData.companyId !== undefined) {
        try {
          const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              query: REQ_APPROVED_VENDOR_LOOKUP_QUERY.loc.source.body,
              variables: {company: formData.companyId}
            })
          });
          const data = await response.json();
          const supplierOptions = data.data.approvedVendors.map(s => ({
            value: s.VendorNum,
            label: s.VendorName,
            CurrencyCode: s.CurrencyCode
          }));
          setVendors(supplierOptions);
        } catch (error) {
          console.error('Error fetching suppliers:', error);
        }
      }
    }

    fetchVendors();
  }, [formData.companyId]);

  // Fetch analysis codes
  useEffect(() => {
    async function fetchAnalysisCodes() {
      if (formData.companyId !== undefined) {
        try {
          const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              query: REQ_ANALYSIS_CODE_LOOKUP_QUERY.loc.source.body,
              variables: {company: formData.companyId}
            })
          });
          const data = await response.json();
          const analysisCodeOptions = data.data.acAnalysisCodes.map(a => ({
            value: a.AnalysisCd_AnalysisCode,
            label: `${a.AnalysisCd_AnalysisCode} - ${a.AnalysisCd_Description}`
          }));
          setAnalysisCodes(analysisCodeOptions);
        } catch (error) {
          console.error('Error fetching analysis codes:', error);
        }
      }
    }
    fetchAnalysisCodes();
  }, [formData.companyId]);

  const formatDateForSubmission = date => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${month}-${day}-${year}`;
  };

  const convertFileToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file input change
  const handleFileChange = async (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const fileBase64 = await convertFileToBase64(file);
      const newReqDetails = [...formData.reqDetail];
      newReqDetails[index].file = file;
      newReqDetails[index].fileBase64 = fileBase64;
      setFormData({...formData, reqDetail: newReqDetails});
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    setIsSubmitting(true);
    const validationError = validateForm();
    if (validationError) {
      setFormErrors(validationError);
      setShowErrorAlert(true);
      setIsSubmitting(false);
      return;
    }

    const reqDetailWithFormattedValues = formData.reqDetail.map(item => {
      const {
        LineTotal,
        vendorOptions,
        DueDate,
        file,
        fileBase64,
        ...rest
      } = item;

      // Use sensible numeric defaults so we never send NaN (JSON.stringify turns NaN into null, which can cause "properties are null" in Epicor)
      const orderQty = parseInt(item.OrderQty, 10);
      const unitCost = parseFloat(item.UnitCost, 10);
      const vendorNum = parseInt(item.VendorNum, 10);
      const xOrderQty = parseInt(item.XOrderQty, 10);

      return {
        ...rest,
        DueDate: DueDate ? formatDateForSubmission(DueDate) : '',
        OrderQty: Number.isNaN(orderQty) ? 1 : orderQty,
        UnitCost: Number.isNaN(unitCost) ? 0 : unitCost,
        VendorNum: Number.isNaN(vendorNum) ? 0 : vendorNum,
        XOrderQty: Number.isNaN(xOrderQty) ? 1 : xOrderQty,
        Taxable: item.Taxable ? 'TRUE' : 'FALSE',
        OverrideCurrency: item.OverrideCurrency ? 'TRUE' : 'FALSE'
      };
    });

    try {
      const response = await fetch('/api/graphql', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          query: CREATE_REQUISITION_MUTATION,
          variables: {
            company: formData.companyId,
            efxLibraryID: 'requisitions',
            efxFunctionID: 'requisition-creation',
            params: {
              requestorID: formData.requestorID ?? '',
              approverID: formData.approverID ?? '',
              CompanyName: formData.CompanyName ?? '',
              Address1: formData.Address1 ?? '',
              Address2: formData.Address2 ?? '',
              Address3: formData.Address3 ?? '',
              City: formData.City ?? '',
              State: formData.State ?? '',
              Zip: formData.Zip ?? '',
              Country: formData.Country ?? '',
              CC: formData.CC ?? '',
              reqDetail: {
                reqDetail: reqDetailWithFormattedValues
              }
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Requisition submission failed');
      }

      const jsonResponse = await response.json();

      // GraphQL returns { data: null, errors: [...] } or { data: { createRequisition: null }, errors: [...] } when the mutation fails
      if (jsonResponse.errors && jsonResponse.errors.length > 0) {
        const msg = jsonResponse.errors[0].message || jsonResponse.errors[0].errorMessage;
        throw new Error(msg || 'Requisition submission failed');
      }

      const createResult = jsonResponse.data?.createRequisition;
      const requisitionId = createResult?.output;

      if (!requisitionId) {
        throw new Error(
          jsonResponse.errors?.[0]?.message ||
            'There was an error retrieving Requisition ID. The server did not return a result.'
        );
      }

      // Check if requisitionId starts with "Ice.Common.BusinessObjectException:"
      if (requisitionId.startsWith('Ice.Common.BusinessObjectException:')) {
        throw new Error('An error occurred while submitting the requisition');
      }

      // Upload attachments for each line item with a file
      for (const [index, item] of formData.reqDetail.entries()) {
        if (item.fileBase64) {
          await uploadAttachment(
            requisitionId,
            item.fileBase64,
            item.file.name,
            index + 1
          );
        }
      }

      setShowSuccessModal(true);
      setFormData(initialState);
      onFormSubmitSuccess(requisitionId);
    } catch (error) {
      setFormErrors(
        error.message || 'An error occurred while submitting the requisition'
      );
      setShowErrorAlert(true);
      onFormSubmitError(
        error.message || 'An error occurred while submitting the requisition'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadAttachment = async (
    requisitionId,
    fileBase64,
    fileName,
    reqLine,
    docTypeID = 'ReqAttch',
    table = 'ReqDetail'
  ) => {
    let message = requisitionId;
    const requisitionIdMatch = message.match(/Requisition # (\d+)/);

    if (!requisitionIdMatch) {
      console.log('Requisition ID not found in the message.');
    }
    try {
      const response = await fetch('/api/graphql', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          query: CREATE_REQUISITION_ATTACHMENT_MUTATION,
          variables: {
            company: formData.companyId,
            efxLibraryID: 'requisitions',
            efxFunctionID: 'requisition-attachement',
            params: {
              DocTypeID: docTypeID,
              FileName: fileName,
              DrawDesc: fileName,
              ReqNum: requisitionIdMatch[1],
              ReqLine: reqLine,
              Table: table,
              FileStream: fileBase64
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Attachment upload failed');
      }

      // const jsonResponse = await response.json();
      // console.log("Attachment upload response:", jsonResponse);
    } catch (error) {
      console.error('Error uploading attachment:', error);
    }
  };

  return (
    <>
      {showErrorAlert && validationErrorAlert}
      <form onSubmit={handleSubmit} className="form-container">
        {/* Company Selector */}
        <div className="form-section">
          <div>
            <label htmlFor="companySelect">File Requisition in Company: </label>
            <ReactSelect
              id="companySelect"
              name="Company"
              value={formData.companyId}
              onChange={handleCompanyChange}
              options={companyDropdownOptions}
              placeholder="Select your Company"
              noResultsText="Company Not Found"
              isLoading={reqCompsLoading}
              required={true}
            />
          </div>
        </div>

        {/* Address Section */}
        <div className="form-section">
          <h5>Ship To</h5>
          <div className="row">
            <div className="col-md-6">
              <label htmlFor="CompanyName">Company Name</label>
              <input
                type="text"
                className="form-control"
                id="CompanyName"
                name="CompanyName"
                value={formData.CompanyName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="Address1">Address 1</label>
              <input
                type="text"
                className="form-control"
                id="Address1"
                name="Address1"
                value={formData.Address1}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <label htmlFor="Address2">Address 2</label>
              <input
                type="text"
                className="form-control"
                id="Address2"
                name="Address2"
                value={formData.Address2}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="Address3">Address 3</label>
              <input
                type="text"
                className="form-control"
                id="Address3"
                name="Address3"
                value={formData.Address3}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <label htmlFor="City">City</label>
              <input
                type="text"
                className="form-control"
                id="City"
                name="City"
                value={formData.City}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="State">State/Province</label>
              <input
                type="text"
                className="form-control"
                id="State"
                name="State"
                value={formData.State}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="Zip">Zip</label>
              <input
                type="text"
                className="form-control"
                id="Zip"
                name="Zip"
                value={formData.Zip}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <label htmlFor="Country">Country</label>
              <ReactSelect
                id="Country"
                name="Country"
                value={
                  countryOptions.find(
                    option => option.value === formData.Country
                  ) || null
                }
                onChange={handleCountryChange}
                options={countryOptions}
                placeholder="Select Country"
                required
              />
            </div>
          </div>
        </div>

        {/* Requisition Items */}
        {formData.reqDetail.map((item, index) => (
          <div key={index} className="form-section requisition-item mb-4">
            <div className="item-header">
              <h5>Item {index + 1}</h5>
              {index > 0 && (
                <button
                  type="button"
                  className="btn btn-danger remove-circle-btn"
                  onClick={() => removeItem(index)}>
                  &times;
                </button>
              )}
            </div>

            {/* Part Number and Description */}
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`PartNum-${index}`}>Part Number</label>
                <input
                  type="text"
                  className="form-control"
                  id={`PartNum-${index}`}
                  name="PartNum"
                  value={item.PartNum}
                  onChange={e => handleInputChange(e, index)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label htmlFor={`LineDesc-${index}`}>Description</label>
                <textarea
                  type="text"
                  className="form-control req-desc"
                  id={`LineDesc-${index}`}
                  name="LineDesc"
                  value={item.LineDesc}
                  onChange={e => handleInputChange(e, index)}
                  rows="4"
                  required
                />
              </div>
            </div>

            {/* Due Date */}
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`DueDate-${index}`}>Due Date</label>
                <input
                  type="date"
                  className="form-control"
                  id={`DueDate-${index}`}
                  name="DueDate"
                  value={item.DueDate ? item.DueDate : ''}
                  onChange={e => handleDueDateChange(e, index)}
                  required
                />
              </div>
            </div>

            {/* Quantity and Unit Cost */}
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`OrderQty-${index}`}>Quantity</label>
                <input
                  type="number"
                  className="form-control"
                  id={`OrderQty-${index}`}
                  name="OrderQty"
                  value={item.OrderQty}
                  onChange={e => handleInputChange(e, index)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label htmlFor={`UnitCost-${index}`}>Unit Cost</label>
                <div className="d-flex align-items-center">
                  <input
                    type="number"
                    className="form-control"
                    id={`UnitCost-${index}`}
                    name="UnitCost"
                    value={item.UnitCost}
                    onChange={e => handleInputChange(e, index)}
                    required
                  />
                  <Tooltip
                    placement="top"
                    overlay="Unit of Measure"
                    trigger={['hover']}>
                    <span
                      className="ml-2 uom-label"
                      style={{cursor: 'pointer'}}>
                      EA
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Line Total */}
            <div className="row">
              <div className="col-md-6">
                <label>Line Total</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.LineTotal ? item.LineTotal.toFixed(2) : 0}
                  readOnly
                />
              </div>
            </div>

            {/* Taxable Checkbox */}
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`Taxable-${index}`}>Taxable</label>
                <input
                  type="checkbox"
                  id={`Taxable-${index}`}
                  name="Taxable"
                  checked={item.Taxable}
                  onChange={e => handleTaxableChange(e, index)}
                />
              </div>
            </div>

            {/* Class and Vendor */}
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`Class-${index}`}>Class</label>
                <ReactSelect
                  id={`Class-${index}`}
                  name="Class"
                  value={
                    partClasses.find(option => option.value === item.Class) ||
                    null
                  }
                  onChange={selectedOption =>
                    handleSelectChange(selectedOption, 'Class', index)
                  }
                  options={partClasses}
                  placeholder="Select Class"
                  required
                />
              </div>
              <div className="col-md-6">
                <label htmlFor={`VendorNum-${index}`}>Vendor</label>
                <ReactSelect
                  id={`VendorNum-${index}`}
                  name="VendorNum"
                  value={item.VendorNum}
                  onChange={selectedOption =>
                    handleSelectChange(selectedOption, 'VendorNum', index)
                  }
                  options={vendors}
                  placeholder="Select Vendor"
                  required
                />
              </div>
            </div>

            {/* Currency and Override Checkbox */}
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`CurrencyCode-${index}`}>Currency</label>
                <ReactSelect
                  id={`CurrencyCode-${index}`}
                  name="CurrencyCode"
                  value={{
                    value: item.CurrencyCode,
                    label: item.CurrencyCode
                  }}
                  onChange={selectedOption =>
                    handleSelectChange(selectedOption, 'CurrencyCode', index)
                  }
                  options={currencyOptions}
                  placeholder="Select Currency"
                  required
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`OverrideCurrency-${index}`}>
                  Override Currency
                </label>
                <input
                  type="checkbox"
                  id={`OverrideCurrency-${index}`}
                  name="OverrideCurrency"
                  checked={item.OverrideCurrency}
                  onChange={e => handleOverrideCurrencyChange(e, index)}
                />
              </div>
            </div>

            {/* Project & Job and Analysis Code */}
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`JobNum-${index}`}>Project & Job</label>
                <ReactSelect
                  id={`JobNum-${index}`}
                  name="JobNum"
                  value={item.JobNum}
                  onChange={selectedOption =>
                    handleSelectChange(selectedOption, 'JobNum', index)
                  }
                  options={jobOptions}
                  placeholder="Select Project & Job"
                  required
                />
              </div>
              <div className="col-md-6">
                <label htmlFor={`AnalysisCode-${index}`}>Analysis Code</label>
                <ReactSelect
                  id={`AnalysisCode-${index}`}
                  name="AnalysisCode"
                  value={item.AnalysisCode}
                  onChange={selectedOption =>
                    handleSelectChange(selectedOption, 'AnalysisCode', index)
                  }
                  options={analysisCodes}
                  placeholder="Select Analysis Code"
                  required
                />
              </div>
            </div>
            <div className='row'>
              <div className="col-md-6">
            <Tooltip
              placement="top"
              overlay={
                <span>
                  Comments for individual req lines
                </span>
              }>
              <label htmlFor={`Comments-${index}`}>Comment</label>
            </Tooltip>
            <textarea
              className="form-control"
              placeholder="Enter up to 1,000 characters"
              id={`Comments-${index}`}
              type="text"
              name="Comments"
              value={item.Comments}
              onChange={e => handleInputChange(e, index)}
              rows="4"
            />
            {item.Comments.length > 1000 && <Alert error={`Over character limit: ${item.Comments.length}/1,000`}/>}
          </div>

            </div>
            <div className="row">
              <div className="col-md-6">
                <label htmlFor={`FileUpload-${index}`}>Attachment</label>
                <input
                  type="file"
                  className="form-control"
                  id={`FileUpload-${index}`}
                  onChange={e => handleFileChange(e, index)}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add New Line Button */}
        <div className="d-flex justify-content-start add-another-button">
          <button
            type="button"
            className="btn btn-success mb-3"
            onClick={addNewLine}>
            Add Another Item
          </button>
        </div>

        {/* Requestor and Approver Section */}
        <div className="form-section">
          <h5>Requester and Approver</h5>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="requestorID">Requestor ID</label>
              <input
                type="text"
                className="form-control"
                id="requestorID"
                name="requestorID"
                value={formData.requestorID}
                disabled
                required
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="approverID">Approver</label>
              <ReactSelect
                id="approverID"
                name="approverID"
                value={
                  approvers.find(
                    option => option.value === formData.approverID
                  ) || null
                }
                onChange={selectedOption =>
                  setFormData({
                    ...formData,
                    approverID: selectedOption.value
                  })
                }
                options={approvers}
                placeholder="Select an Approver"
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h5>CC Emails (Optional)</h5>
          <label htmlFor="CC">Separate multiple emails with a semicolon:</label>
          <input
            type="text"
            className={`form-control ${ccError ? 'is-invalid' : ''}`}
            id="CC"
            name="CC"
            placeholder="email@domain.com; second@domain.com"
            value={formData.CC}
            onChange={handleInputChange}
          />
          {ccError && <small className="text-danger">{ccError}</small>}
        </div>

        {/* Submit Button */}
        <div className="d-flex justify-content-end">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}>
            Submit Requisition
          </button>
        </div>
        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Success</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowSuccessModal(false)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p>Requisition was submitted successfully!</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowSuccessModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <style jsx>{`
          .form-section {
            border: 1px solid #d3d3d3;
            padding: 20px;
            margin-bottom: 20px;
            border-radius: 5px;
            background-color: #f9f9f9;
          }

          .item-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .btn-success {
            margin-top: 1em;
            background-color: #28a745 !important;
            border-color: #28a745 !important;
            color: #fff !important;
          }

          .btn-primary {
            margin-top: 2em;
            background-color: #007bff !important;
            border-color: #007bff !important;
            color: #fff !important;
          }

          .btn-danger {
            margin-top: 1em;
            background-color: #dc3545 !important;
            border-color: #dc3545 !important;
            color: #fff !important;
          }

          .btn-success:hover {
            background-color: #218838 !important;
            border-color: #1e7e34 !important;
          }

          .btn-primary:hover {
            background-color: #0056b3 !important;
            border-color: #004085 !important;
          }

          .btn-danger:hover {
            background-color: #c82333 !important;
            border-color: #bd2130 !important;
          }

          .remove-circle-btn {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            color: #fff !important;
            background-color: #dc3545 !important;
            border: none;
            cursor: pointer;
          }

          .remove-circle-btn:hover {
            background-color: #c82333 !important;
          }

          .req-desc {
            resize: none !important;
          }

          .add-another-button {
            margin-top: 0;
            margin-bottom: 20px;
          }

          .uom-label {
            align-content: center;
            font-weight: bold;
            color: #007bff;
            margin-left: 10px;
          }
        `}</style>
      </form>
    </>
  );
}

export default RequisitionForm;
